import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrder, getCustomers, getAssignableUsers, getAllEmployees, getSystemSettings, aiScheduleOrder } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ImageUpload } from '../components/ImageUpload';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, Trash2, Camera, Users, X, Repeat, BrainCircuit, Sparkles, CheckCircle2 } from 'lucide-react';

export const NewOrderPage = () => {
    const [customers, setCustomers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]); // All employees including those without smartphone
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [images, setImages] = useState([]);
    const [aiSchedulingEnabled, setAiSchedulingEnabled] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiSuggestion, setAiSuggestion] = useState(null);
    const [formData, setFormData] = useState({
        customer_id: '',
        title: '',
        description: '',
        service_address: '',
        planned_date: '',
        deadline: '',
        external_order_no: '',
        work_items: [],
        helper_user_ids: [],
        billing_type: 'HOURLY',  // HOURLY (Stundenbasis) or FIXED (Pauschal)
        is_recurring: false,
        recurring_type: '',
        recurring_end_date: '',
        recurring_count: '',
        assign_to_user_id: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            const [customersRes, employeesRes, settingsRes] = await Promise.all([
                getCustomers(),
                getAssignableUsers(),
                getSystemSettings()
            ]);
            setCustomers(customersRes.data);
            setEmployees(employeesRes.data);
            setAiSchedulingEnabled(settingsRes.data.ai_scheduling_enabled || false);
            
            // Try to load all employees for helper selection (optional, may fail on older deployments)
            try {
                const allEmployeesRes = await getAllEmployees();
                setAllEmployees(allEmployeesRes.data);
            } catch {
                // Fallback to assignable employees if all-employees endpoint not available
                setAllEmployees(employeesRes.data);
            }
        } catch (err) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    const handleAiSchedule = async () => {
        if (!formData.customer_id) {
            toast.error('Bitte wählen Sie zuerst einen Kunden aus');
            return;
        }
        if (!formData.title.trim()) {
            toast.error('Bitte geben Sie einen Titel ein');
            return;
        }
        
        setAiLoading(true);
        setAiSuggestion(null);
        
        try {
            const res = await aiScheduleOrder({
                customer_id: formData.customer_id,
                title: formData.title,
                description: formData.description,
                service_address: formData.service_address,
                deadline: formData.deadline,
                work_items: formData.work_items.filter(item => item.description.trim())
            });
            
            if (res.data.success) {
                setAiSuggestion(res.data);
                toast.success('KI-Vorschlag erhalten');
            } else {
                toast.error(res.data.reasoning || 'KI-Planung fehlgeschlagen');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler bei der KI-Planung');
        } finally {
            setAiLoading(false);
        }
    };

    const acceptAiSuggestion = () => {
        if (!aiSuggestion) return;
        
        setFormData({
            ...formData,
            planned_date: aiSuggestion.suggested_date || '',
            assign_to_user_id: aiSuggestion.suggested_employee_id || ''
        });
        toast.success('KI-Vorschlag übernommen');
    };

    const addWorkItem = () => {
        setFormData({
            ...formData,
            work_items: [
                ...formData.work_items,
                { description: '' }
            ]
        });
    };

    const updateWorkItem = (index, description) => {
        const newItems = [...formData.work_items];
        newItems[index] = { description };
        setFormData({ ...formData, work_items: newItems });
    };

    const removeWorkItem = (index) => {
        const newItems = formData.work_items.filter((_, i) => i !== index);
        setFormData({ ...formData, work_items: newItems });
    };

    const handleCustomerChange = (customerId) => {
        const customer = customers.find(c => c.id === customerId);
        setFormData({
            ...formData,
            customer_id: customerId,
            service_address: customer?.service_address || customer?.billing_address || ''
        });
    };

    const addHelper = (userId) => {
        if (!userId || formData.helper_user_ids.includes(userId)) return;
        setFormData({
            ...formData,
            helper_user_ids: [...formData.helper_user_ids, userId]
        });
    };

    const removeHelper = (userId) => {
        setFormData({
            ...formData,
            helper_user_ids: formData.helper_user_ids.filter(id => id !== userId)
        });
    };

    const getHelperName = (userId) => {
        const user = allEmployees.find(e => e.id === userId) || employees.find(e => e.id === userId);
        return user?.name || 'Unbekannt';
    };

    // Use allEmployees for helper selection (includes those without smartphone)
    // Exclude the assigned employee from the helper list
    const availableHelpers = allEmployees.filter(e => 
        !formData.helper_user_ids.includes(e.id) && 
        e.id !== formData.assigned_to
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.customer_id) {
            toast.error('Bitte wählen Sie einen Kunden');
            return;
        }
        if (!formData.title.trim()) {
            toast.error('Bitte geben Sie einen Titel ein');
            return;
        }
        // Validate recurring settings
        if (formData.is_recurring) {
            if (!formData.planned_date) {
                toast.error('Für wiederkehrende Aufträge ist ein geplantes Datum erforderlich');
                return;
            }
            if (!formData.recurring_type) {
                toast.error('Bitte wählen Sie einen Wiederholungsintervall');
                return;
            }
        }
        setSaving(true);
        try {
            // Filter out empty work items and add images
            const dataToSend = {
                ...formData,
                work_items: formData.work_items.filter(item => item.description.trim()),
                images: images.map(img => ({
                    filename: img.filename,
                    data: img.data
                })),
                generate_recurring: formData.is_recurring,
                recurring_count: formData.recurring_count ? parseInt(formData.recurring_count) : null,
                assign_to_user_id: formData.assign_to_user_id || null
            };
            const res = await createOrder(dataToSend);
            
            // Check if recurring orders were created
            if (res.data.recurring_orders_created) {
                toast.success(`Auftrag erstellt + ${res.data.recurring_orders_created} wiederkehrende Aufträge`);
                navigate(`/orders/${res.data.order.id}`);
            } else {
                toast.success('Auftrag erstellt');
                navigate(`/orders/${res.data.id || res.data.order?.id}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="new-order-page">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/orders')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Neuer Auftrag</h1>
                    <p className="text-muted-foreground">Erstellen Sie einen neuen Auftrag</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Customer Selection */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Kunde</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Kunde auswählen *</Label>
                            <Select value={formData.customer_id} onValueChange={handleCustomerChange}>
                                <SelectTrigger data-testid="customer-select">
                                    <SelectValue placeholder="Kunde auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {customers.map(customer => (
                                        <SelectItem key={customer.id} value={customer.id}>
                                            {customer.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Order Details */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Auftragsdetails</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="title">Titel *</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) => setFormData({...formData, title: e.target.value})}
                                placeholder="z.B. Wartung Heizungsanlage"
                                required
                                data-testid="title-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Abrechnungsart</Label>
                            <Select 
                                value={formData.billing_type} 
                                onValueChange={(v) => setFormData({...formData, billing_type: v})}
                            >
                                <SelectTrigger data-testid="billing-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="HOURLY">Stundenbasis</SelectItem>
                                    <SelectItem value="FIXED">Pauschal</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                {formData.billing_type === 'HOURLY' 
                                    ? 'Arbeitszeit wird beim Abschließen erfasst' 
                                    : 'Keine Zeiterfassung beim Abschließen'}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="external_order_no">Externe Auftragsnummer</Label>
                                <Input
                                    id="external_order_no"
                                    value={formData.external_order_no}
                                    onChange={(e) => setFormData({...formData, external_order_no: e.target.value})}
                                    placeholder="z.B. Kundennummer oder Referenz"
                                    data-testid="external-order-no-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="planned_date">Geplantes Datum</Label>
                                <div className="relative">
                                    <Input
                                        id="planned_date"
                                        type="date"
                                        value={formData.planned_date}
                                        onChange={(e) => setFormData({...formData, planned_date: e.target.value})}
                                        data-testid="planned-date-input"
                                        className={formData.planned_date ? 'pr-10' : ''}
                                    />
                                    {formData.planned_date && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-destructive/10"
                                            onClick={() => setFormData({...formData, planned_date: ''})}
                                            data-testid="clear-planned-date-btn"
                                        >
                                            <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="deadline">Erledigen bis</Label>
                                <div className="relative">
                                    <Input
                                        id="deadline"
                                        type="date"
                                        value={formData.deadline}
                                        onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                                        data-testid="deadline-input"
                                        className={formData.deadline ? 'pr-10' : ''}
                                    />
                                    {formData.deadline && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-destructive/10"
                                            onClick={() => setFormData({...formData, deadline: ''})}
                                            data-testid="clear-deadline-btn"
                                        >
                                            <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                        </Button>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground">Optional: Frist für die Fertigstellung</p>
                            </div>
                        </div>
                        
                        {/* Recurring Order Options */}
                        <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Repeat className="h-5 w-5 text-primary" />
                                    <Label htmlFor="is_recurring" className="font-medium cursor-pointer">
                                        Wiederkehrender Auftrag
                                    </Label>
                                </div>
                                <Switch
                                    id="is_recurring"
                                    checked={formData.is_recurring}
                                    onCheckedChange={(checked) => setFormData({...formData, is_recurring: checked})}
                                    data-testid="recurring-switch"
                                />
                            </div>
                            
                            {formData.is_recurring && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                                    <div className="space-y-2">
                                        <Label>Wiederholung *</Label>
                                        <Select 
                                            value={formData.recurring_type} 
                                            onValueChange={(v) => setFormData({...formData, recurring_type: v})}
                                        >
                                            <SelectTrigger data-testid="recurring-type-select">
                                                <SelectValue placeholder="Intervall wählen..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="weekly">Wöchentlich</SelectItem>
                                                <SelectItem value="biweekly">Alle 2 Wochen</SelectItem>
                                                <SelectItem value="monthly">Monatlich</SelectItem>
                                                <SelectItem value="quarterly">Vierteljährlich</SelectItem>
                                                <SelectItem value="yearly">Jährlich</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="recurring_end_date">Enddatum</Label>
                                        <Input
                                            id="recurring_end_date"
                                            type="date"
                                            value={formData.recurring_end_date}
                                            onChange={(e) => setFormData({...formData, recurring_end_date: e.target.value})}
                                            data-testid="recurring-end-date-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="recurring_count">Anzahl (max.)</Label>
                                        <Input
                                            id="recurring_count"
                                            type="number"
                                            min="1"
                                            max="52"
                                            value={formData.recurring_count}
                                            onChange={(e) => setFormData({...formData, recurring_count: e.target.value})}
                                            placeholder="z.B. 12"
                                            data-testid="recurring-count-input"
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {formData.is_recurring && (
                                <p className="text-xs text-muted-foreground">
                                    Es werden automatisch alle wiederkehrenden Aufträge bis zum Enddatum oder der maximalen Anzahl erstellt.
                                </p>
                            )}
                            
                            {/* Employee Assignment for Recurring */}
                            {formData.is_recurring && (
                                <div className="pt-2 border-t">
                                    <div className="space-y-2">
                                        <Label>Direkt zuweisen an (optional)</Label>
                                        <Select 
                                            value={formData.assign_to_user_id} 
                                            onValueChange={(v) => setFormData({...formData, assign_to_user_id: v === 'none' ? '' : v})}
                                        >
                                            <SelectTrigger data-testid="assign-to-select">
                                                <SelectValue placeholder="Mitarbeiter wählen..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Keine Zuweisung</SelectItem>
                                                {employees.map(emp => (
                                                    <SelectItem key={emp.id} value={emp.id}>
                                                        {emp.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            Wenn Sie einen Mitarbeiter auswählen, werden alle wiederkehrenden Aufträge direkt zugewiesen und im Kalender angezeigt.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Employee Assignment for Non-Recurring (used by AI suggestion) */}
                        {!formData.is_recurring && (
                            <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                                <Label>Direkt zuweisen an Mitarbeiter</Label>
                                <Select 
                                    value={formData.assign_to_user_id || 'none'} 
                                    onValueChange={(v) => setFormData({...formData, assign_to_user_id: v === 'none' ? '' : v})}
                                >
                                    <SelectTrigger data-testid="assign-to-user-select">
                                        <SelectValue placeholder="Mitarbeiter wählen..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Keine Zuweisung (Entwurf)</SelectItem>
                                        {employees.map(emp => (
                                            <SelectItem key={emp.id} value={emp.id}>
                                                {emp.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Optional: Weisen Sie den Auftrag direkt einem Mitarbeiter zu. Der Auftrag erscheint dann sofort im Kalender.
                                </p>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="service_address">Leistungsadresse</Label>
                            <Textarea
                                id="service_address"
                                value={formData.service_address}
                                onChange={(e) => setFormData({...formData, service_address: e.target.value})}
                                rows={2}
                                data-testid="service-address-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">Beschreibung</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Allgemeine Beschreibung des Auftrags..."
                                rows={3}
                                data-testid="description-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* AI Scheduling - Only show if enabled */}
                {aiSchedulingEnabled && !formData.is_recurring && (
                    <Card className="border-primary/30 bg-primary/5">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <BrainCircuit className="h-5 w-5 text-primary" />
                                KI-Auftragsplanung
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Lassen Sie die KI basierend auf Mitarbeiter-Verfügbarkeit, Arbeitszeiten und bestehenden Terminen 
                                den optimalen Mitarbeiter und das beste Datum vorschlagen.
                            </p>
                            
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleAiSchedule}
                                disabled={aiLoading || !formData.customer_id || !formData.title}
                                className="w-full"
                                data-testid="ai-schedule-btn"
                            >
                                {aiLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        KI analysiert...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        KI-Planung starten
                                    </>
                                )}
                            </Button>
                            
                            {/* AI Suggestion Display */}
                            {aiSuggestion && aiSuggestion.success && (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                                    <div className="flex items-center gap-2 text-green-700 font-medium">
                                        <CheckCircle2 className="h-5 w-5" />
                                        KI-Vorschlag
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="text-muted-foreground">Mitarbeiter:</span>
                                            <p className="font-medium">{aiSuggestion.suggested_employee_name || 'Nicht verfügbar'}</p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Datum:</span>
                                            <p className="font-medium">
                                                {aiSuggestion.suggested_date ? 
                                                    new Date(aiSuggestion.suggested_date).toLocaleDateString('de-DE', {
                                                        weekday: 'long',
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric'
                                                    }) : 'Nicht verfügbar'}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground italic">
                                        "{aiSuggestion.reasoning}"
                                    </p>
                                    <Button
                                        type="button"
                                        onClick={acceptAiSuggestion}
                                        className="w-full"
                                        data-testid="accept-ai-suggestion-btn"
                                    >
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        Vorschlag übernehmen
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Work Items */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg">Arbeitspositionen</CardTitle>
                        <Button type="button" variant="outline" size="sm" onClick={addWorkItem} data-testid="add-work-item-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Position hinzufügen
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {formData.work_items.length === 0 ? (
                            <p className="text-center py-8 text-muted-foreground">
                                Keine Arbeitspositionen vorhanden. Fügen Sie einzelne Arbeiten hinzu, die erledigt werden sollen.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {formData.work_items.map((item, index) => (
                                    <div key={item.id || `work-item-${index}`} className="flex gap-2 items-start" data-testid={`work-item-${index}`}>
                                        <span className="flex items-center justify-center w-8 h-10 text-sm font-medium text-muted-foreground">
                                            {index + 1}.
                                        </span>
                                        <Input
                                            value={item.description}
                                            onChange={(e) => updateWorkItem(index, e.target.value)}
                                            placeholder="z.B. Rasen mähen, Hecke schneiden..."
                                            className="flex-1"
                                            data-testid={`work-item-input-${index}`}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeWorkItem(index)}
                                            data-testid={`remove-work-item-${index}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Helper Workers */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Mitwirkende Mitarbeiter
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Mitarbeiter hinzufügen</Label>
                            <Select onValueChange={addHelper} value="">
                                <SelectTrigger data-testid="helper-select">
                                    <SelectValue placeholder="Mitarbeiter auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableHelpers.map(emp => (
                                        <SelectItem key={emp.id} value={emp.id}>
                                            {emp.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {formData.helper_user_ids.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {formData.helper_user_ids.map(userId => (
                                    <div 
                                        key={userId}
                                        className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                                    >
                                        <span>{getHelperName(userId)}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeHelper(userId)}
                                            className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Fügen Sie Mitarbeiter hinzu, die bei diesem Auftrag mitgewirkt haben.
                        </p>
                    </CardContent>
                </Card>

                {/* Images */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Camera className="h-5 w-5" />
                            Bilder
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ImageUpload 
                            images={images} 
                            setImages={setImages} 
                            maxImages={10}
                        />
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex gap-4 justify-end">
                    <Button type="button" variant="outline" onClick={() => navigate('/orders')} data-testid="cancel-btn">
                        Abbrechen
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="create-order-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Auftrag erstellen
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default NewOrderPage;
